/*  UI  */
import { Box, Flex } from '@auviknetworks/iceberg-ui';

/*  Packages  */
import { evaluate } from 'cel-js';
import React, { useEffect, useMemo, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';

import { useDashboardContext } from '../DashboardContext';
import {
    ChartPanel,
    CustomPanel,
    EmptyPanel,
    EntityPanel,
    GroupPanel,
    KPIPanel,
    TablePanel
} from '../Panels';
import { Panel } from '../Panels/Panel';
import {
    type ChartPanel as ChartPanelType,
    type CustomPanel as CustomPanelType,
    type Dashboard as DashboardType,
    type EmptyPanel as EmptyPanelType,
    type EntityPanel as EntityPanelType,
    type GroupPanel as GroupPanelType,
    type KPIPanel as KPIPanelType,
    type Panel as PanelType,
    type TablePanel as TablePanelType
} from '../types';
import { Filters } from './Filters';
import { Search } from './Search';
import { scaleMasonryLayout } from './scaleMasonryLayout';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

/**
 * Checks if a panel is a base panel.
 *
 * @param panel - The panel to check.
 * @returns True if the panel is a base panel, false otherwise.
 */
function isBasePanel(panel: PanelType): boolean {
    return typeof panel?.id === 'string' && typeof panel?.type === 'string';
}

/**
 * Renders a panel based on its type.
 *
 * @param panel - The panel to render.
 * @param dashboard - The dashboard.
 */
const Factory = ({ panel }: { panel: PanelType }) => {
    if (!isBasePanel(panel)) return null;

    if (panel.meta?.render === false) return null;

    switch (panel.type) {
        case 'chart':
            return <ChartPanel panel={panel as ChartPanelType} />;
        case 'custom':
            return <CustomPanel panel={panel as CustomPanelType} />;
        case 'empty':
            return <EmptyPanel panel={panel as EmptyPanelType} />;
        case 'entity':
            return <EntityPanel panel={panel as EntityPanelType} />;
        case 'group':
            return (
                <GroupPanel
                    panel={panel as GroupPanelType}
                    Workspace={Workspace}
                />
            );
        case 'kpi':
            return <KPIPanel panel={panel as KPIPanelType} />;
        case 'table':
            return <TablePanel panel={panel as TablePanelType} />;
        default:
            return <Panel panel={panel} />;
    }
};

export const Workspace = ({
    schema
}: {
    schema: DashboardType | GroupPanelType;
}) => {
    const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
    const [selectedTag, setSelectedTag] = useState<string | null>(null);
    const [search, setSearch] = useState<string>('');
    const {
        context,
        handleLayoutChange,
        dashboardState: { isEditing, autoAdjust, isFiltrable, isSearchable }
    } = useDashboardContext();

    // Reset the selected tag when editing
    useEffect(() => {
        if (isEditing) {
            setSelectedTag(null);
        }
    }, [isEditing]);

    // Filter those panels that should not render given a condition
    const filteredPanelsByRender = useMemo<PanelType[]>(
        () =>
            schema.panels.filter((panel) => {
                const { render = true } = panel?.meta || {};
                const shouldRender =
                    typeof render === 'string'
                        ? (evaluate(render, context.variables) as boolean)
                        : render;
                return shouldRender;
            }),
        [context.variables, schema.panels]
    );

    // Gather unique tags from panels if dashboard.tags is not provided
    const allPanelTags = useMemo(() => {
        if (schema.tags && schema.tags.length > 0) return schema.tags;
        const tagSet = new Set<string>();
        filteredPanelsByRender.forEach((panel) => {
            if (isBasePanel(panel) && Array.isArray(panel.tags)) {
                panel.tags.forEach((tag) => tagSet.add(tag));
            }
        });
        return Array.from(tagSet);
    }, [filteredPanelsByRender, schema.tags]);

    const filteredPanelsIds: string[] | null = useMemo(
        () =>
            filteredPanelsByRender
                .filter((panel) => {
                    if (!isBasePanel(panel)) return false;
                    const matchesTag = selectedTag
                        ? panel.tags?.includes(selectedTag)
                        : true;
                    const matchesSearch =
                        search.length > 0
                            ? (panel.title ?? '')
                                  .toLowerCase()
                                  .includes(search.toLowerCase()) ||
                              panel.description
                                  ?.toLowerCase()
                                  .includes(search.toLowerCase())
                            : true;
                    return matchesTag && matchesSearch;
                })
                .map((panel) => panel.id),
        [selectedTag, search, filteredPanelsByRender]
    );

    const filteredPanels = useMemo<PanelType[]>(
        () =>
            filteredPanelsIds
                ? filteredPanelsByRender.filter((panel) =>
                      filteredPanelsIds.includes(panel.id)
                  )
                : filteredPanelsByRender,
        [filteredPanelsIds, filteredPanelsByRender]
    );

    const filteredLayout = useMemo(
        () =>
            filteredPanelsIds
                ? schema.layout.filter((layoutItem) =>
                      filteredPanelsIds.includes(layoutItem.i)
                  )
                : schema.layout,
        [filteredPanelsIds, schema.layout]
    );

    const { layouts, columns, breakpoints } = useMemo(
        () =>
            scaleMasonryLayout({
                columns: schema?.columns as Record<string, number>,
                layout: filteredLayout,
                shouldAdjust: !isEditing && autoAdjust
            }),
        [autoAdjust, filteredLayout, isEditing, schema.columns]
    );

    return (
        <Box data-testid="dashboardWorkspace">
            <Flex
                data-testid="dashboardWorkspaceSearchFilters"
                sx={{ gap: '8px' }}
            >
                {isSearchable && <Search onChange={setSearch} />}
                {isFiltrable && (
                    <Filters
                        tags={allPanelTags}
                        selected={selectedTag}
                        onSelect={setSelectedTag}
                        currentBreakpoint={currentBreakpoint}
                    />
                )}
            </Flex>

            <ResponsiveReactGridLayout
                autoSize
                breakpoints={breakpoints}
                className={`dashboard-layout breakpoint-${currentBreakpoint} ${
                    isEditing ? 'editing' : ''
                }`}
                cols={columns}
                containerPadding={[0, 0]}
                data-testid="workspace"
                draggableCancel=".dashboard-layout"
                draggableHandle=".dashboard-panel-title"
                isDraggable={isEditing}
                isResizable={isEditing}
                layouts={layouts}
                margin={[24, 24]}
                onBreakpointChange={setCurrentBreakpoint}
                onLayoutChange={handleLayoutChange}
                preventCollision={false}
                resizeHandles={['ne', 'nw', 'se', 'sw']}
                rowHeight={60}
                width={isEditing ? 1200 : undefined}
            >
                {filteredPanels.map((panel) => (
                    // The React-Layout-Grid is not working with the custom component, so we need to use a div.
                    <Box
                        data-testid={`workspaceFilterPanelsWrapper_${panel.id}`}
                        key={panel.id}
                        className={`dashboard-panel type-${
                            panel.type || 'unknown'
                        }`}
                        sx={{ '&:hover': { zIndex: 1 }, zIndex: 0 }}
                    >
                        <Factory key={panel.id} panel={panel} />
                    </Box>
                ))}
            </ResponsiveReactGridLayout>
        </Box>
    );
};
